import React, { useState } from 'react';

import { useSelector } from 'react-redux';
import {
	CircularProgress,
	TextField,
	TextFieldProps,
	Box,
	Chip,
	Checkbox,
	Typography,
	ListItem,
	ListItemText,
	ListItemAvatar,
} from '@mui/material';
import Autocomplete, { createFilterOptions } from '@mui/material/Autocomplete';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import AddIcon from '@mui/icons-material/Add';

import { updateLocaleField } from 'common/utils/languageUtils';
import errorHandler from 'common/services/errorHandler';
import { Category } from 'common/types';
import { useTranslation } from 'services/localization/useTranslation';
import { FilterOptionsState } from '@mui/material/useAutocomplete';
import * as StockSelectors from 'selectors/StockSelectors';
import * as ShopSelectors from 'selectors/ShopSelectors';
import { newFirestoreId } from 'common/utils/newRentalUtils';
import { api } from 'common/frontend/api';

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

interface Option {
	id: string;
	label: string;
	selected: boolean;
	disabled?: boolean;
	data?: string;
}

interface Props {
	value: string[] | undefined;
	onChange: (value: string[]) => void;
	disabled?: boolean;
	disableCreate?: boolean;
	TextFieldProps?: TextFieldProps;
}

const customFilter = createFilterOptions({
	matchFrom: 'start',
	stringify: (option: Option) => option.label,
});

const ADD_CATEGORY = '__ADD_CATEGORY__';

const CategorySelect = (props: Props) => {
	const { value = [], onChange } = props;
	const categoriesById = useSelector(StockSelectors.categoriesById);
	const categoriesLoading = useSelector(StockSelectors.categoriesLoading);
	const maxCategoryOrderIndex = useSelector(StockSelectors.maxCategoryOrderIndex);
	const shopId = useSelector(ShopSelectors.activeShopId);
	const shopDefaultLanguage = useSelector(ShopSelectors.activeShopDefaultLanguage);
	const categoryIds = Object.keys(categoriesById);
	const { t, getTranslation } = useTranslation();

	const [inputValue, setInputValue] = useState<string>('');
	const [createLoading, setCreateLoading] = useState<boolean>(false);
	const [createError, setCreateError] = useState<string | undefined>();

	const options: Option[] = categoryIds.map((id) => ({
		id,
		label: getTranslation(categoriesById[id].name),
		selected: false,
	}));

	const valueOptions = options.filter((option) => value.indexOf(option.id) !== -1);

	const handleCreate = async (categoryName: string) => {
		if (!shopId) return;
		setCreateLoading(true);
		const newCategoryId = newFirestoreId();
		const newCategory: Category = {
			id: newCategoryId,
			shopId,
			name: updateLocaleField(undefined, categoryName, shopDefaultLanguage),
			imageUrl: null,
			terms: null,
			orderIndex: maxCategoryOrderIndex + 1,
			bookingBuffer: {
				daysBefore: 0,
				minutesBefore: 0,
			},
		};
		try {
			await api().productCategories.doc(newCategory.id).set(newCategory);
			onChange(value.concat(newCategory.id));
			setInputValue('');
		} catch (err) {
			errorHandler.report(err);
			setCreateError('Error creating category');
		} finally {
			setCreateLoading(false);
		}
	};

	const handleChange = (_: any, selectedOptions: Option[]) => {
		setCreateError(undefined);
		const addItem = selectedOptions.find((o) => o.id === ADD_CATEGORY);

		if (addItem?.data) {
			handleCreate(addItem.data);
		}

		onChange(
			selectedOptions.map((option: Option) => option.id).filter((id) => id !== ADD_CATEGORY),
		);
	};

	const renderOption = (
		props: React.HTMLAttributes<HTMLLIElement>,
		option: Option,
		{ selected }: { selected: boolean },
	) => {
		if (option.id === ADD_CATEGORY) {
			return (
				<ListItem key={ADD_CATEGORY} {...props}>
					<ListItemAvatar>
						<AddIcon />
					</ListItemAvatar>
					<ListItemText
						primary={
							t('product.categorySelect.createCategoryTitle', 'Create category') +
							`${inputValue ? ': ' + inputValue : ''} `
						}
						secondary={option.label}
					/>
				</ListItem>
			);
		}
		return (
			<ListItem key={option.id} {...props}>
				<Checkbox
					icon={icon}
					color="primary"
					checkedIcon={checkedIcon}
					style={{ marginRight: 8 }}
					checked={selected}
				/>
				<Typography variant="body1">{option.label}</Typography>
			</ListItem>
		);
	};

	const filterOptions = (options: Option[], state: FilterOptionsState<Option>) => {
		if (createLoading || createError) return [];
		const filtered = customFilter(options, state);

		if (!props.disableCreate) {
			const trimmedInput = state.inputValue.trimStart();

			if (!!trimmedInput) {
				if (!options.some((option) => option.label === state.inputValue)) {
					filtered.push({
						id: ADD_CATEGORY,
						label: '',
						data: trimmedInput,
						selected: false,
					});
				}
			} else {
				filtered.push({
					id: ADD_CATEGORY,
					label: t(
						'product.categorySelect.createCategoryHelper',
						'Type the name of the category to create',
					),
					disabled: true,
					selected: false,
				});
			}
		}

		return filtered;
	};

	return (
		<Autocomplete
			autoComplete
			autoHighlight
			blurOnSelect
			multiple
			disableCloseOnSelect
			openOnFocus
			disabled={props.disabled || createLoading}
			loading={createLoading}
			loadingText={
				<ListItem>
					<ListItemAvatar>
						<CircularProgress color="primary" size={20} />
					</ListItemAvatar>
					<ListItemText
						primary={t('product.categorySelect.createCategoryLoading', 'Creating category') + '...'}
					/>
				</ListItem>
			}
			id="category-select"
			options={options}
			getOptionLabel={(option) => option.label}
			value={valueOptions}
			onChange={handleChange}
			inputValue={inputValue}
			onInputChange={(event, newInputValue, reason) => {
				if (reason === 'input') {
					setInputValue(newInputValue.trimStart());
				}
			}}
			filterOptions={filterOptions}
			renderOption={renderOption}
			getOptionDisabled={(option: Option) => option.disabled === true}
			renderInput={(params) => (
				<TextField
					{...params}
					label={t('product.categories', 'Product categories')}
					placeholder={
						value.length === 0
							? !options.length
								? t(
										'product.categorySelect.createCategoryHelper',
										'Type the name of the category to create',
								  )
								: t('product.typeToSearchCategories', 'Type to search categories')
							: ''
					}
					fullWidth
					margin="normal"
					variant="standard"
					{...props.TextFieldProps}
					InputProps={{
						...params.InputProps,
						endAdornment: (
							<React.Fragment>
								{categoriesLoading ||
									(createLoading && (
										<Box mr={2}>
											<CircularProgress color="primary" size={20} />
										</Box>
									))}
								{params.InputProps.endAdornment}
							</React.Fragment>
						),
					}}
					onFocus={() => setCreateError(undefined)}
				/>
			)}
			renderTags={(value: Option[], getTagProps) =>
				value.map((option: Option, index: number) => (
					<Chip label={option.label} {...getTagProps({ index: index as number })} />
				))
			}
			noOptionsText={
				createError ? (
					<Typography variant="body2" color="error">
						{t('product.categorySelect.createCategoryError', 'Error creating category')}
					</Typography>
				) : (
					<Typography variant="body2">
						{t('product.categorySelect.noOptions', 'No results')}
					</Typography>
				)
			}
		/>
	);
};

export default CategorySelect;
